<template>
  <div class="echarts-renderer">
    <div 
      v-for="(chartData, index) in chartsData" 
      :key="'chart-' + index" 
      class="chart-container"
    >
      <!-- loading状态 -->
      <div v-if="chartData.loading" class="chart-loading">
        <i class="el-icon-loading"></i>
        <span>{{ chartData.message || '图表正在加载...' }}</span>
      </div>
      
      <!-- 正常图表 -->
      <div 
        v-else
        ref="chart" 
        :style="{ width: chartWidth, height: chartHeight }"
        class="chart"
      ></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'EchartsRenderer',
  props: {
    chartsData: {
      type: Array,
      default: () => []
    },
    chartWidth: {
      type: String,
      default: '100%'
    },
    chartHeight: {
      type: String,
      default: '300px'
    }
  },
  data() {
    return {
      chartInstances: [],
      resizeObserver: null
    }
  },
  watch: {
    chartsData: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.renderCharts()
            // 渲染完成后重新设置ResizeObserver监听
            this.setupResizeObserver()
          })
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.renderCharts()
    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleResize)
    // 添加MutationObserver监听容器大小变化
    this.setupResizeObserver()
  },
  beforeDestroy() {
    // 清理监听器
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.disposeCharts()
  },
  methods: {
    handleResize() {
      // 统一处理所有图表的重置大小
      this.chartInstances.forEach(chart => {
        if (chart && !chart.isDisposed()) {
          chart.resize()
        }
      })
    },
    
    renderCharts() {
      // 先销毁之前的图表
      this.disposeCharts()
      
      // 渲染新图表
      if (this.$refs.chart && this.chartsData.length > 0) {
        this.chartsData.forEach((chartData, index) => {
          if (this.$refs.chart[index]) {
            try {
              // 尝试解析JSON格式的echarts配置
              let options = chartData
              
              // 如果是对象且包含options属性，则使用options
              if (chartData && typeof chartData === 'object' && chartData.options) {
                options = chartData.options
              } else if (typeof chartData === 'string') {
                // 如果是字符串，尝试解析JSON
                try {
                  options = JSON.parse(chartData)
                } catch (e) {
                  // 如果不是有效的JSON，尝试作为JavaScript代码执行
                  try {
                    // 创建一个安全的执行环境
                    const func = new Function(`return ${chartData}`)
                    options = func()
                  } catch (jsError) {
                    console.error('无法解析echarts配置:', jsError)
                    return
                  }
                }
              }
              
              const chartInstance = echarts.init(this.$refs.chart[index])
              chartInstance.setOption(options)
              this.chartInstances.push(chartInstance)
              
              // 确保ResizeObserver监听新创建的图表容器
              this.setupResizeObserver()
              
              // 图表渲染完成后发出事件通知父组件
              this.$emit('chart-rendered')
            } catch (error) {
              console.error('渲染echarts图表失败:', error)
            }
          }
        })
      }
    },
    
    disposeCharts() {
      this.chartInstances.forEach(chart => {
        if (chart && !chart.isDisposed()) {
          chart.dispose()
        }
      })
      this.chartInstances = []
    },
    
    setupResizeObserver() {
      // 使用ResizeObserver监听容器大小变化
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver((entries) => {
          // 防抖处理，避免频繁重绘
          clearTimeout(this.resizeTimer)
          this.resizeTimer = setTimeout(() => {
            this.handleResize()
          }, 100)
        })
        
        // 监听所有图表容器
        if (this.$refs.chart) {
          this.$refs.chart.forEach(container => {
            if (container) {
              this.resizeObserver.observe(container)
            }
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.echarts-renderer {
  width: 100%;
}

.chart-container {
  margin: 10px 0;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}

.chart {
  min-height: 200px;
}
</style>